import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Cell,
  Text,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { RiskGroupType } from 'store/modules/Firm/types';
import { dummyData } from '../Utils';
import { makeStyles } from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import { existingVsRecommendedIndex } from 'components/Settings/Palette/utils';
import { radiusHorizontal } from 'utils/charts';

const useStyles = makeStyles({
  legend: {
    width: '100%'
  },
  legendBox: {
    width: '1rem',
    height: '1rem',
    marginRight: '1rem',
    borderRadius: '50%'
  }
});

interface PoliciesRiskScoreProps {
  data: Array<{
    type: string;
    cash_value: number;
    percentage_value: number;
    number: string;
    portfolio_type: string;
  }>;
  isAnimationActive?: boolean;
  riskGroup: RiskGroupType;
  footer?: string;
  existingOnly?: boolean;
}

const CustomizedAxisTick = ({ x, y, payload, number }: any) => {
  return (
    <Text
      x={x}
      y={y}
      width={140}
      textAnchor="end"
      verticalAnchor="middle"
      style={{ fontSize: '0.75rem' }}
    >
      {`${payload.value} (${number || ''})`}
    </Text>
  );
};

const Tick = ({ payload: { value }, ...rest }: any) => (
  <text {...rest} className="bar-chart-tick" dy={0}>
    {value}
  </text>
);

const PoliciesRiskScore = ({
  data,
  isAnimationActive = true,
  riskGroup,
  footer,
  existingOnly = false
}: PoliciesRiskScoreProps) => {
  const theme = useTheme() as any;
  const classes = useStyles();
  const isDataProvided = data && data.length > 0;
  const policyData = isDataProvided ? data : dummyData.policyCharges;

  const dataToRender = () => {
    if (existingOnly) {
      return policyData.filter(item => item.portfolio_type === 'existing');
    }

    return policyData;
  };

  return (
    <>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={dataToRender()}
          margin={{
            top: 5,
            right: 80,
            left: isDataProvided ? 50 : -30,
            bottom: 35
          }}
          layout="vertical"
        >
          <XAxis
            type="number"
            tickLine={false}
            tick={<Tick />}
            orientation="top"
            axisLine={false}
            tickCount={12}
            allowDecimals={false}
            domain={[1, riskGroup]}
          />

          <YAxis
            dataKey="type"
            type="category"
            tickLine={false}
            tick={(args: any) => {
              const policyData = dataToRender()[args.index];
              return (
                <CustomizedAxisTick
                  {...args}
                  number={policyData && policyData.number}
                />
              );
            }}
            width={100}
            axisLine={false}
          />

          <Bar
            dataKey="risk_score"
            fill="#D95045"
            barSize={50}
            minPointSize={3}
            isAnimationActive={isAnimationActive}
            radius={radiusHorizontal}
          >
            {dataToRender().map((entry, index) => {
              return (
                <Cell
                  key={`cell-${index}`}
                  fill={
                    theme.palette.baseColors.charts['comparisons'][
                      existingVsRecommendedIndex(entry.portfolio_type)
                    ]
                  }
                />
              );
            })}
          </Bar>
          <Legend
            wrapperStyle={{ bottom: '20px', left: '15px' }}
            content={
              <div className={classes.legend}>
                <div style={{ display: 'flex' }}>
                  <div
                    className={classes.legendBox}
                    style={{
                      backgroundColor:
                        theme.palette.baseColors.charts['comparisons'][
                          existingVsRecommendedIndex('existing')
                        ]
                    }}
                  />
                  <div>Existing</div>
                </div>
                {!existingOnly && (
                  <div style={{ display: 'flex' }}>
                    <div
                      className={classes.legendBox}
                      style={{
                        backgroundColor:
                          theme.palette.baseColors.charts['comparisons'][
                            existingVsRecommendedIndex('recommended')
                          ]
                      }}
                    />
                    <div>Recommended</div>
                  </div>
                )}
              </div>
            }
          />
        </BarChart>
      </ResponsiveContainer>
      {footer && <span style={{ marginLeft: '1rem' }}>{footer}</span>}
    </>
  );
};

export default PoliciesRiskScore;
